import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Instagram, Video, Globe, Users, TrendingUp,
  Download, RefreshCw, Loader2, Linkedin, Youtube, ShoppingBag, Clock,
  MoreHorizontal, Pencil, Trash2, Pause, Play, X, CheckCircle2,
  UserSearch, Mail, Phone, Briefcase, FlaskConical, ExternalLink, Share2,
  MessageSquare, ThumbsUp, ThumbsDown, Package, Plus, ChevronDown, ChevronUp,
} from 'lucide-react';
import { api } from '../utils/api';
import { useIsMobile } from '../utils/useIsMobile';
import { useAuth } from '../context/AuthContext';
import ShareBrandModal from '../components/ShareBrandModal';
import { BrandTagEditor } from '../components/BrandTags';
import OrganicShell from '../components/organic/OrganicShell';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const STROKE = 2.75; // Organic's lucide weight

// Chart.js draws on canvas, which cannot resolve CSS variables — these
// mirror the Organic tokens (--color-accent-2 & friends).
const CHART_LINE = '#7a8a5e'; // var(--color-accent-2)
const CHART_FILL = 'rgba(122, 138, 94, 0.16)';
const CHART_GRID = 'rgba(32, 30, 29, 0.08)'; // muted divider
const CHART_TICKS = '#645c50'; // var(--color-neutral-700)

// ── Consumer-sentiment card helpers ──────────────────────────────────
// Map the discrete label / numeric score to Organic accent colors
// (positive = sage accent-2, negative = clay accent, Mixed = default).
function sentimentLabelColor(label) {
  if (label === 'Positive') return 'var(--color-accent-2-700)';
  if (label === 'Negative') return 'var(--color-accent-700)';
  return undefined;
}

function sentimentScoreColor(score) {
  if (score == null) return undefined;
  if (score >= 65) return 'var(--color-accent-2-700)';
  if (score <= 40) return 'var(--color-accent-700)';
  return undefined;
}

// A sentiment theme chip. Themes are either bare strings (older rows) or
// {theme, sources} objects; when a theme carries source URLs the chip
// becomes a link to the backing review/thread/article (first source;
// "+N" when several), so complaints and praise are traceable.
function SentimentThemeChip({ theme, kind }) {
  const label = typeof theme === 'string' ? theme : theme?.theme;
  const sources = (theme && typeof theme === 'object' && Array.isArray(theme.sources))
    ? theme.sources
    : [];
  if (!label) return null;
  const cls = kind === 'positive' ? 'tag tag-accent-2' : 'tag tag-accent';
  if (sources.length) {
    return (
      <a
        href={sources[0]}
        target="_blank"
        rel="noreferrer"
        title={sources.join('\n')}
        className={cls}
        style={{ gap: 5, textDecoration: 'none', cursor: 'pointer' }}
      >
        {label}
        <ExternalLink size={11} strokeWidth={STROKE} />
        {sources.length > 1 && (
          <span style={{ fontSize: 10, opacity: 0.7 }}>+{sources.length - 1}</span>
        )}
      </a>
    );
  }
  return <span className={cls}>{label}</span>;
}

// Compact counts: 1.2K / 3.40M, plain below a thousand. Module-scoped so
// the sentiment helpers below can share it with the component.
function formatNumber(num) {
  if (!num) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString();
}

// Review count for the header stat tile. Falls back to the brand's own
// store count when that is what the score rests on — review_volume means
// "independent reviews" and is null/0 for a first-party-scored brand.
function sentimentVolumeLabel(sentiment) {
  const volume = sentiment?.score_basis === 'first_party'
    ? sentiment?.first_party_review_volume
    : sentiment?.review_volume;
  if (volume == null) return '';
  const suffix = sentiment?.score_basis === 'first_party' ? ' own-site reviews' : ' reviews';
  return ` · ${formatNumber(volume)}${suffix}`;
}

// Why a run came back "Insufficient". The backend deliberately withholds
// a precise number on thin evidence, but the run still researched the
// brand — so the card says which of the three thin cases it hit instead
// of the one flat "not enough reviews" line, which read like a failure.
function insufficientReason(sentiment) {
  const fpVolume = sentiment?.first_party_review_volume;
  const volume = sentiment?.review_volume;
  const plural = (n) => (n === 1 ? '' : 's');

  if (sentiment?.score_basis === 'first_party') {
    return fpVolume != null
      ? `No independent reviews found, and only ${formatNumber(fpVolume)} review${plural(fpVolume)} on the brand's own store — too thin to score.`
      : "No independent reviews found, and the brand's own store carries too few to score.";
  }
  if (volume != null && volume > 0) {
    return `Only ${formatNumber(volume)} public review${plural(volume)} found across retailers, Reddit and press — below the threshold to score.`;
  }
  return 'No public product reviews found yet on retailers, Reddit or press.';
}

function formatRelativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const diffMs = Date.now() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 1) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  const diffYears = Math.floor(diffDays / 365);
  return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
}

// Former names worth labelling: drops entries that just restate the
// current name (a pre-rename research run could store the old name in
// both columns — "Mixoloshe (formerly Mixoloshe)" tells the operator
// nothing).
function formerNamesLabel(brand) {
  if (!brand?.former_names) return null;
  const squash = (v) => (v || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const current = squash(brand.name);
  const names = brand.former_names
    .split(',')
    .map((n) => n.trim())
    .filter((n) => n && squash(n) !== current);
  return names.length ? names.join(', ') : null;
}

const ROLE_LABELS = {
  founder: 'Founder',
  leadership: 'Leadership',
  rnd: 'R&D / Product',
  procurement: 'Procurement / Buyer',
  other: 'Other',
};
const ROLE_OPTIONS = ['founder', 'leadership', 'rnd', 'procurement', 'other'];
// Order suggestion sections are shown in.
const SUGGESTION_ROLES = ['rnd', 'procurement', 'leadership'];

// Small links shown for a person: email (free/inline), phone, LinkedIn.
function ContactLinks({ email, phone, linkedinUrl }) {
  if (!email && !phone && !linkedinUrl) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', fontSize: 12 }}>
      {email && (
        <a href={`mailto:${email}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Mail size={12} strokeWidth={STROKE} /> {email}
        </a>
      )}
      {phone && (
        <a href={`tel:${phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Phone size={12} strokeWidth={STROKE} /> {phone}
        </a>
      )}
      {linkedinUrl && (
        <a href={linkedinUrl} target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Linkedin size={12} strokeWidth={STROKE} /> LinkedIn
        </a>
      )}
    </div>
  );
}

function roleBadge(role) {
  return (
    <span className="tag tag-accent-2" style={{ alignSelf: 'flex-start', fontSize: 11 }}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

// A contact already saved to the brand: links + Get mobile + Remove.
function SavedContactCard({ contact, busyKey, onGetPhone, onDelete }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      gap: 'var(--space-3)', padding: 'var(--space-3)',
      borderRadius: 'var(--radius-md)', background: 'var(--color-accent-2-100)',
    }}>
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 14 }}>
          <strong>{contact.full_name || 'Contact'}</strong>
          {contact.title && <span style={{ opacity: 0.7 }}> — {contact.title}</span>}
        </span>
        {roleBadge(contact.role)}
        <ContactLinks email={contact.email} phone={contact.phone} linkedinUrl={contact.linkedin_url} />
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flex: 'none' }}>
        {!contact.phone && contact.linkedin_url && (
          <button
            type="button"
            className="btn btn-secondary"
            style={{ fontSize: 12, whiteSpace: 'nowrap' }}
            onClick={() => onGetPhone(contact.id)}
            disabled={busyKey === `phone:${contact.id}`}
            title="Fetch this contact's mobile number — the costly lookup (uses external enrichment credits), separate from the free search."
          >
            {busyKey === `phone:${contact.id}`
              ? <Loader2 size={13} strokeWidth={STROKE} className="spin" />
              : <Phone size={13} strokeWidth={STROKE} />} Get mobile
          </button>
        )}
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          style={{ color: 'var(--color-accent-700)' }}
          onClick={() => onDelete(contact.id)}
          disabled={busyKey === `delete:${contact.id}`}
          title="Remove this saved contact."
          aria-label="Remove this saved contact"
        >
          {busyKey === `delete:${contact.id}`
            ? <Loader2 size={13} strokeWidth={STROKE} className="spin" />
            : <Trash2 size={13} strokeWidth={STROKE} />}
        </button>
      </div>
    </div>
  );
}

// A scout candidate with a Save action. When `fixedRole` is null the row
// shows a role dropdown (used in the full roster); otherwise it saves
// under that role (used in the suggestion sections).
function CandidateRow({ cand, fixedRole, saved, busyKey, onSave, onFindEmail }) {
  const [role, setRole] = useState(fixedRole || cand.roles?.[0] || 'other');
  const [lookup, setLookup] = useState(null); // { email, verified, guess }
  const email = cand.business_email || lookup?.email || null;
  const guess = !email && lookup ? lookup.guess : null;
  const rowKey = cand.linkedin_url || cand.full_name || '';
  const saving = busyKey === `save:${rowKey}`;
  const finding = busyKey === `email:${rowKey}`;
  // Once we've looked up and got nothing (no email, no guess), stop offering.
  const triedAndEmpty = lookup && !email && !guess;
  const canFindEmail = !email && !triedAndEmpty && (cand.linkedin_url || cand.full_name);

  const findEmail = async () => {
    // Pass the row's selected role + title so a verified hit is
    // auto-saved under the right tags.
    const r = await onFindEmail({ ...cand, role });
    if (r) setLookup(r);
  };

  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      gap: 'var(--space-3)', padding: 'var(--space-3)',
      borderRadius: 'var(--radius-md)', border: '1px solid var(--color-divider)',
      opacity: saved ? 0.6 : 1,
    }}>
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 14 }}>
          <strong>{cand.full_name || 'Unknown'}</strong>
          {cand.title && <span style={{ opacity: 0.7 }}> — {cand.title}</span>}
        </span>
        {cand.seniority && (
          <span className="tag tag-neutral" style={{ alignSelf: 'flex-start', fontSize: 11 }}>
            {cand.seniority}
          </span>
        )}
        <ContactLinks email={email} linkedinUrl={cand.linkedin_url} />
        {guess && (
          <span style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
            <Mail size={12} strokeWidth={STROKE} /> {guess}
            <span className="tag tag-accent" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              guess · unverified
            </span>
          </span>
        )}
        {triedAndEmpty && (
          <span style={{ fontSize: 12, opacity: 0.65 }}>
            No email found.
          </span>
        )}
        {canFindEmail && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={findEmail}
            disabled={finding}
            style={{ alignSelf: 'flex-start', fontSize: 12, fontFamily: 'var(--font-body)' }}
            title="Look up this person's verified business email (LinkedIn, then name + company domain). Only billed if a verified email is found; falls back to an unverified guess."
          >
            {finding
              ? <Loader2 size={12} strokeWidth={STROKE} className="spin" />
              : <Mail size={12} strokeWidth={STROKE} />}
            {finding ? 'Looking up…' : 'Find email (billed only if found)'}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flex: 'none' }}>
        {!fixedRole && !saved && (
          <select
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            style={{ width: 'auto', minHeight: 30, fontSize: 12, padding: '2px 10px' }}
          >
            {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        )}
        <button
          type="button"
          className="btn btn-secondary"
          style={{ fontSize: 12, whiteSpace: 'nowrap' }}
          onClick={() => onSave(role, { ...cand, business_email: email || guess })}
          disabled={saving || saved}
        >
          {saving
            ? <Loader2 size={12} strokeWidth={STROKE} className="spin" />
            : (saved ? <CheckCircle2 size={12} strokeWidth={STROKE} /> : null)}
          {saved ? 'Saved' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// Domain override for the scout: edit when the brand's stored website
// points at the wrong company, then Re-scout.
function CompanyDomainEditor({ initial, pending, onRescout }) {
  const [domain, setDomain] = useState(initial || '');
  useEffect(() => { setDomain(initial || ''); }, [initial]);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap', fontSize: 13 }}>
      <span style={{ opacity: 0.65 }}>Company searched:</span>
      <input
        className="input"
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        placeholder="company.com"
        title="Domain (or company name) to search. Edit if the brand's stored website points at the wrong company."
        style={{ width: 'auto', flex: '1 1 180px', minWidth: 160, minHeight: 32, fontSize: 13 }}
      />
      <button
        type="button"
        className="btn btn-secondary"
        style={{ fontSize: 13, flex: 'none' }}
        onClick={() => onRescout(domain.trim())}
        disabled={pending || !domain.trim()}
      >
        {pending
          ? <Loader2 size={13} strokeWidth={STROKE} className="spin" />
          : <RefreshCw size={13} strokeWidth={STROKE} />} Re-scout
      </button>
    </div>
  );
}

// The interactive outreach-contacts card: saved contacts at top, then
// (after a scout) keyword-bucketed suggestions and the full roster.
function OutreachContacts({ brand, candidates, busyKey, scoutPending, message, onScout, onRescout, onSave, onFindEmail, onGetPhone, onDelete }) {
  const [rosterOpen, setRosterOpen] = useState(false);
  const saved = Array.isArray(brand.contacts) ? brand.contacts : [];
  const empty = !candidates && saved.length === 0 && !brand.contact_status;

  const savedUrls = new Set(saved.map((c) => c.linkedin_url).filter(Boolean));
  const founder = candidates?.founder;
  const suggestions = candidates?.suggestions || {};
  const roster = candidates?.roster || [];

  return (
    /* `#contacts` is where Movers' and the phone header's "Find contact"
       land — see the hash effect in BrandDetail. */
    <div id="contacts" className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-4)', scrollMarginTop: 'calc(var(--o-topbar-h) + var(--space-3))' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <UserSearch size={18} strokeWidth={STROKE} /> Outreach contacts
        </h4>
        <button
          type="button"
          className="btn btn-primary"
          style={{ fontSize: 13 }}
          onClick={() => onScout()}
          disabled={scoutPending}
          title="Scout outreach candidates via MoltSets: a short ranked list per role (Founder, Procurement/Buyer, R&D/Product). Cheap — business emails come inline; mobile numbers are fetched per-contact on demand."
        >
          {scoutPending
            ? <><Loader2 size={14} strokeWidth={STROKE} className="spin" /> Finding candidates…</>
            : <><UserSearch size={14} strokeWidth={STROKE} /> Find contacts</>}
        </button>
      </div>

      {candidates && (
        <CompanyDomainEditor
          initial={candidates.domain || ''}
          pending={scoutPending}
          onRescout={onRescout}
        />
      )}

      {message && (
        <Notice kind={message.kind}>{message.text}</Notice>
      )}

      {empty && (
        <p style={{ margin: 0, fontSize: 13, opacity: 0.65 }}>
          No contacts yet. Use Find contacts to scout a short ranked list per
          role (Founder, Procurement/Buyer, R&D/Product) — business emails come
          inline; mobile numbers are fetched per-contact on demand.
        </p>
      )}

      {saved.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span className="card-kicker">Saved</span>
          {saved.map((c) => (
            <SavedContactCard key={c.id} contact={c} busyKey={busyKey} onGetPhone={onGetPhone} onDelete={onDelete} />
          ))}
        </div>
      )}

      {candidates && (
        <>
          {founder && (founder.full_name || founder.linkedin_url) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <span className="card-kicker" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Users size={12} strokeWidth={STROKE} /> Founder
              </span>
              <CandidateRow
                cand={{ ...founder, business_email: null }}
                fixedRole="founder"
                saved={!!founder.linkedin_url && savedUrls.has(founder.linkedin_url)}
                busyKey={busyKey}
                onSave={onSave}
                onFindEmail={onFindEmail}
              />
            </div>
          )}

          {SUGGESTION_ROLES.map((role) => {
            const list = suggestions[role] || [];
            if (list.length === 0) return null;
            const icon = role === 'rnd' ? <FlaskConical size={12} strokeWidth={STROKE} />
              : role === 'procurement' ? <Briefcase size={12} strokeWidth={STROKE} />
                : <Users size={12} strokeWidth={STROKE} />;
            return (
              <div key={role} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <span className="card-kicker" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  {icon} Suggested: {ROLE_LABELS[role]}
                </span>
                {list.map((cand, i) => (
                  <CandidateRow
                    key={cand.linkedin_url || `${role}-${i}`}
                    cand={cand}
                    fixedRole={role}
                    saved={!!cand.linkedin_url && savedUrls.has(cand.linkedin_url)}
                    busyKey={busyKey}
                    onSave={onSave}
                    onFindEmail={onFindEmail}
                  />
                ))}
              </div>
            );
          })}

          {roster.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setRosterOpen((v) => !v)}
                style={{ alignSelf: 'flex-start', fontSize: 13 }}
              >
                {rosterOpen
                  ? <ChevronUp size={14} strokeWidth={STROKE} />
                  : <ChevronDown size={14} strokeWidth={STROKE} />}
                Full roster ({roster.length}) — save anyone with any role
              </button>
              {rosterOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  {roster.map((cand, i) => (
                    <CandidateRow
                      key={cand.linkedin_url || `roster-${i}`}
                      cand={cand}
                      fixedRole={null}
                      saved={!!cand.linkedin_url && savedUrls.has(cand.linkedin_url)}
                      busyKey={busyKey}
                      onSave={onSave}
                      onFindEmail={onFindEmail}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {candidates.notes?.length > 0 && (
            <div style={{ fontSize: 11, opacity: 0.55 }}>
              {candidates.notes.join(' · ')}
            </div>
          )}
        </>
      )}

      {brand.contact_enriched_at && (
        <span style={{ fontSize: 11, opacity: 0.55, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Clock size={12} strokeWidth={STROKE} /> Last updated {formatRelativeTime(brand.contact_enriched_at)}
        </span>
      )}
    </div>
  );
}

// Team members following this brand — everyone a report has been shared
// with, and the date they first received it. Populated by the Share
// action. Each follower can be removed (un-followed) — re-sharing later
// re-adds them with a fresh follow date.
function TeamFollowers({ brand, onBrandUpdate, onShare }) {
  const followers = Array.isArray(brand.followers) ? brand.followers : [];
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const formatSince = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const handleRemove = async (f) => {
    const confirmed = window.confirm(`Remove ${f.email} from this brand's followers?`);
    if (!confirmed) return;
    setBusyId(f.id);
    setError(null);
    try {
      const res = await api.removeBrandFollower(brand.id, f.id);
      onBrandUpdate(res.brand);
    } catch (err) {
      setError(err?.message || 'Removing the follower failed.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
      <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <Users size={18} strokeWidth={STROKE} /> Following this brand
        {followers.length > 0 && (
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 400, opacity: 0.6 }}>
            {followers.length}
          </span>
        )}
      </h4>
      <p style={{ margin: 0, fontSize: 13, opacity: 0.65 }}>
        Everyone the report has been shared with, and when they started receiving it.
      </p>
      {error && <Notice kind="err">{error}</Notice>}
      {followers.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {followers.map((f) => (
            <div
              key={f.id || f.email}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 'var(--space-3)', flexWrap: 'wrap',
                padding: 'var(--space-3)', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-divider)',
              }}
            >
              <a
                href={`mailto:${f.email}`}
                title={f.email}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, minWidth: 0 }}
              >
                <Mail size={14} strokeWidth={STROKE} /> {f.name || f.email}
                {f.name && (
                  <span style={{ fontSize: 12, opacity: 0.65 }}>
                    ({f.email})
                  </span>
                )}
              </a>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, opacity: 0.65 }}>
                  <Clock size={12} strokeWidth={STROKE} /> Since {formatSince(f.followed_since)}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-icon"
                  onClick={() => handleRemove(f)}
                  disabled={busyId === f.id}
                  title="Remove this follower"
                  aria-label="Remove this follower"
                  style={{ width: 30, height: 30 }}
                >
                  {busyId === f.id
                    ? <Loader2 size={14} strokeWidth={STROKE} className="spin" />
                    : <Trash2 size={14} strokeWidth={STROKE} />}
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        className="btn btn-secondary"
        style={{ alignSelf: 'flex-start', fontSize: 13 }}
        onClick={onShare}
        title="Email this brand's intelligence report — profile, links, metrics and discovered contacts — to a commercial colleague."
      >
        <Share2 size={14} strokeWidth={STROKE} /> Share with someone else
      </button>
    </div>
  );
}

// ── Operator comments ────────────────────────────────────────────────

// Free-form annotations — human context ("met their buyer at Expo
// West", tasting notes) that research never rewrites, unlike the
// description in `notes`. Newest first; delete is author-or-super-admin
// (backend-enforced; the button is only shown when the client thinks
// it'll succeed).
function BrandComments({ brand, onBrandUpdate }) {
  const { user, isSuperAdmin } = useAuth();
  const comments = Array.isArray(brand.comments) ? brand.comments : [];
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const canDelete = (c) =>
    isSuperAdmin() || (user?.id != null && String(user.id) === String(c.hub_user_id));

  const handlePost = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    setError(null);
    try {
      const res = await api.addComment(brand.id, draft.trim());
      onBrandUpdate(res.brand);
      setDraft('');
    } catch (err) {
      setError(err?.message || 'Posting the comment failed.');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (comment) => {
    const confirmed = window.confirm('Delete this comment?');
    if (!confirmed) return;
    setBusyId(comment.id);
    setError(null);
    try {
      const res = await api.deleteComment(brand.id, comment.id);
      onBrandUpdate(res.brand);
    } catch (err) {
      setError(err?.message || 'Deleting the comment failed.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
      <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <MessageSquare size={18} strokeWidth={STROKE} /> Comments
        {comments.length > 0 && (
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 400, opacity: 0.6 }}>
            {comments.length}
          </span>
        )}
      </h4>

      {error && <Notice kind="err">{error}</Notice>}

      <div className="field">
        <textarea
          className="input"
          rows={2}
          placeholder="Meeting notes, tasting impressions, next steps…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          style={{ minHeight: 64 }}
        />
      </div>
      <button
        type="button"
        className="btn btn-primary"
        style={{ alignSelf: 'flex-start', fontSize: 13 }}
        onClick={handlePost}
        disabled={posting || !draft.trim()}
      >
        {posting
          ? <><Loader2 size={14} strokeWidth={STROKE} className="spin" /> Posting…</>
          : 'Post comment'}
      </button>

      {comments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
          {comments.map((c) => (
            <div
              key={c.id}
              style={{
                padding: 'var(--space-3)', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-divider)',
                display: 'flex', flexDirection: 'column', gap: 5,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
                <span style={{ fontSize: 12, opacity: 0.7 }}>
                  <strong style={{ opacity: 1 }}>{c.author_name || 'Unknown'}</strong>
                  {' · '}
                  <span title={c.created_at ? new Date(c.created_at).toLocaleString() : undefined}>
                    {formatRelativeTime(c.created_at) || '—'}
                  </span>
                </span>
                {canDelete(c) && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-icon"
                    onClick={() => handleDelete(c)}
                    disabled={busyId === c.id}
                    title="Delete comment"
                    aria-label="Delete comment"
                    style={{ width: 28, height: 28 }}
                  >
                    {busyId === c.id
                      ? <Loader2 size={13} strokeWidth={STROKE} className="spin" />
                      : <Trash2 size={13} strokeWidth={STROKE} />}
                  </button>
                )}
              </div>
              <span style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{c.body}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Products & ingredients card ──────────────────────────────────────

// Add/edit form for one product. Ingredients are entered one per line
// (or comma-separated) and stored as an ordered list — label order
// matters on a beverage label, so the textarea preserves it.
function ProductForm({ initial, saving, onSubmit, onCancel }) {
  const [name, setName] = useState(initial?.name || '');
  const [variant, setVariant] = useState(initial?.variant || '');
  const [productType, setProductType] = useState(initial?.product_type || '');
  const [ingredientsText, setIngredientsText] = useState(
    (initial?.ingredients || []).join('\n')
  );
  const [sourceUrl, setSourceUrl] = useState(initial?.ingredients_source_url || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const ingredients = ingredientsText
      .split(/[\n,]/)
      .map((i) => i.trim())
      .filter(Boolean);
    onSubmit({
      name: name.trim(),
      variant: variant.trim() || null,
      product_type: productType.trim() || null,
      ingredients,
      ingredients_source_url: sourceUrl.trim() || null,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--space-3)',
        padding: 'var(--space-3)', borderRadius: 'var(--radius-md)',
        background: 'var(--color-neutral-200)',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--space-2)' }}>
        <input
          className="input"
          placeholder="Product name *"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <input
          className="input"
          placeholder="Flavor / variant"
          value={variant}
          onChange={(e) => setVariant(e.target.value)}
        />
        <input
          className="input"
          placeholder="Type (can, bottle…)"
          value={productType}
          onChange={(e) => setProductType(e.target.value)}
        />
      </div>
      <textarea
        className="input"
        rows={4}
        placeholder={'Ingredients in label order, one per line:\nCarbonated water\nOrganic cane sugar\nGinger extract'}
        value={ingredientsText}
        onChange={(e) => setIngredientsText(e.target.value)}
      />
      <input
        className="input"
        placeholder="Source URL (page the ingredient list was read from)"
        value={sourceUrl}
        onChange={(e) => setSourceUrl(e.target.value)}
      />
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <button type="submit" className="btn btn-primary" style={{ fontSize: 13 }} disabled={saving}>
          {saving
            ? <><Loader2 size={14} strokeWidth={STROKE} className="spin" /> Saving…</>
            : 'Save product'}
        </button>
        <button type="button" className="btn btn-secondary" style={{ fontSize: 13 }} onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ProductRow({ product, expanded, onToggle, onEdit, onDelete, busy }) {
  const ingredients = Array.isArray(product.ingredients) ? product.ingredients : [];
  const label = product.variant ? `${product.name} — ${product.variant}` : product.name;
  return (
    <div style={{ borderRadius: 'var(--radius-md)', border: '1px solid var(--color-divider)', overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 'var(--space-3)', padding: 'var(--space-3)', cursor: 'pointer', flexWrap: 'wrap',
        }}
        onClick={onToggle}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap', minWidth: 0 }}>
          <span style={{ fontWeight: 600 }}>{label}</span>
          {product.product_type && (
            <span className="tag tag-neutral" style={{ fontSize: 11 }}>
              {product.product_type}
            </span>
          )}
          <span
            className="tag tag-outline"
            style={{ fontSize: 11 }}
            title={product.source === 'manual'
              ? 'Entered or edited by an operator — deep research never overwrites it.'
              : 'Found by deep research from the source page linked below.'}
          >
            {product.source === 'manual' ? 'manual' : 'researched'}
          </span>
        </div>
        <span style={{ fontSize: 12, opacity: 0.65, display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          {ingredients.length
            ? `${ingredients.length} ingredient${ingredients.length === 1 ? '' : 's'}`
            : 'no ingredient list'}
          {expanded
            ? <ChevronUp size={15} strokeWidth={STROKE} />
            : <ChevronDown size={15} strokeWidth={STROKE} />}
        </span>
      </div>
      {expanded && (
        <div style={{ padding: '0 var(--space-3) var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {ingredients.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {ingredients.map((ing, i) => (
                <span key={i} className="tag tag-outline" style={{ fontSize: 12 }}>
                  {ing}
                </span>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: 13, opacity: 0.65 }}>
              No ingredient list on record for this product yet.
            </span>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {product.ingredients_source_url && (
              <a
                href={product.ingredients_source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
                style={{ fontSize: 12 }}
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink size={13} strokeWidth={STROKE} /> Source
              </a>
            )}
            <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} onClick={onEdit} disabled={busy}>
              <Pencil size={13} strokeWidth={STROKE} /> Edit
            </button>
            <button type="button" className="btn btn-ghost" style={{ fontSize: 12 }} onClick={onDelete} disabled={busy}>
              {busy
                ? <Loader2 size={13} strokeWidth={STROKE} className="spin" />
                : <Trash2 size={13} strokeWidth={STROKE} />} Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductsCard({ brand, onBrandUpdate }) {
  const products = Array.isArray(brand.products) ? brand.products : [];
  const hasResearched = products.some((p) => p.source !== 'manual');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Accordion: expanding one row collapses the others.
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [researching, setResearching] = useState(false);
  const [message, setMessage] = useState(null);

  const handleResearch = async () => {
    // On-demand standalone lineup research (billed). The backend
    // honours a freshness window, so a click right after a run returns
    // the cached lineup instead of re-billing. Refreshing an existing
    // researched lineup forces a from-scratch re-run that also purges
    // stale research-sourced SKUs — confirm so a misclick doesn't
    // silently re-bill (manual rows are never touched either way).
    const force = hasResearched;
    if (force) {
      const confirmed = window.confirm(
        'Refresh the researched lineup? This re-runs product research ' +
        '(billed) and removes previously-researched products that are no ' +
        'longer found. Products you added by hand are never touched.'
      );
      if (!confirmed) return;
    }
    setResearching(true);
    setMessage(null);
    try {
      const res = await api.researchBrandProducts(brand.id, force);
      onBrandUpdate(res.brand);
      setMessage({ kind: 'ok', text: res.message || 'Product research finished.' });
    } catch (err) {
      setMessage({ kind: 'err', text: err?.message || 'Product research failed.' });
    } finally {
      setResearching(false);
    }
  };

  const handleSave = async (payload, productId = null) => {
    setBusyId(productId || 'new');
    setMessage(null);
    try {
      const res = productId
        ? await api.updateProduct(brand.id, productId, payload)
        : await api.saveProduct(brand.id, payload);
      onBrandUpdate(res.brand);
      setAdding(false);
      setEditingId(null);
    } catch (err) {
      setMessage({ kind: 'err', text: err?.message || 'Saving the product failed.' });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (product) => {
    const confirmed = window.confirm(`Remove "${product.name}" from this brand's product list?`);
    if (!confirmed) return;
    setBusyId(product.id);
    setMessage(null);
    try {
      const res = await api.deleteProduct(brand.id, product.id);
      onBrandUpdate(res.brand);
    } catch (err) {
      setMessage({ kind: 'err', text: err?.message || 'Removing the product failed.' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <Package size={18} strokeWidth={STROKE} /> Products & ingredients
          {products.length > 0 && (
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 400, opacity: 0.6 }}>
              {products.length}
            </span>
          )}
        </h4>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ fontSize: 13 }}
            onClick={handleResearch}
            disabled={researching}
          >
            {researching ? (
              <><Loader2 size={14} strokeWidth={STROKE} className="spin" /> Researching…</>
            ) : (
              <><RefreshCw size={14} strokeWidth={STROKE} /> {hasResearched ? 'Refresh lineup' : 'Research lineup'}</>
            )}
          </button>
          {!adding && (
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 13 }}
              onClick={() => { setAdding(true); setEditingId(null); }}
            >
              <Plus size={14} strokeWidth={STROKE} /> Add
            </button>
          )}
        </div>
      </div>

      {message && <Notice kind={message.kind}>{message.text}</Notice>}

      {adding && (
        <ProductForm
          saving={busyId === 'new'}
          onSubmit={(payload) => handleSave(payload)}
          onCancel={() => setAdding(false)}
        />
      )}

      {products.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {products.map((p) => (
            editingId === p.id ? (
              <ProductForm
                key={p.id}
                initial={p}
                saving={busyId === p.id}
                onSubmit={(payload) => handleSave(payload, p.id)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <ProductRow
                key={p.id}
                product={p}
                expanded={expandedId === p.id}
                busy={busyId === p.id}
                onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
                onEdit={() => { setEditingId(p.id); setAdding(false); }}
                onDelete={() => handleDelete(p)}
              />
            )
          ))}
        </div>
      ) : (
        !adding && (
          <p style={{ margin: 0, fontSize: 13, opacity: 0.65, textWrap: 'pretty' }}>
            No products on record yet. Click Research lineup to have AI find
            the brand's products and ingredients on its site or a retailer
            page — or add products by hand.
          </p>
        )
      )}
    </div>
  );
}

// Small ok/err flash panel used across the page's cards.
function Notice({ kind, children }) {
  return (
    <div
      role="status"
      style={{
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-md)',
        fontSize: 13,
        background: kind === 'ok' ? 'var(--color-accent-2-100)' : 'var(--color-accent-200)',
        color: kind === 'ok' ? 'var(--color-accent-2-800)' : 'var(--color-accent-800)',
      }}
    >
      {children}
    </div>
  );
}

function BrandDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const isMobile = useIsMobile();
  const { isSuperAdmin } = useAuth();
  // Phone only: the admin actions that don't fit beside the title.
  const [actionsOpen, setActionsOpen] = useState(false);
  const [brand, setBrand] = useState(null);
  const [growthData, setGrowthData] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platform, setPlatform] = useState('instagram');
  const [days, setDays] = useState(30);
  const [deepResearchPending, setDeepResearchPending] = useState(false);
  const [deepResearchMessage, setDeepResearchMessage] = useState(null);
  const [scoutPending, setScoutPending] = useState(false);
  const [contactMessage, setContactMessage] = useState(null);
  const [candidates, setCandidates] = useState(null);
  // Tracks the in-flight contact action so only that button spins, e.g.
  // 'select:procurement:<linkedin>' or 'phone:rnd'.
  const [contactBusyKey, setContactBusyKey] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activePending, setActivePending] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const [sentimentBusy, setSentimentBusy] = useState(false);
  const [sentimentMessage, setSentimentMessage] = useState(null);

  const handleAnalyzeSentiment = async () => {
    // On-demand product-sentiment run (billed). The backend honours a
    // freshness window, so a click right after a run returns the cached
    // result instead of re-billing. Refreshing an existing score forces
    // a re-run — confirm so a misclick doesn't silently re-bill.
    const force = !!brand?.sentiment;
    if (force) {
      const confirmed = window.confirm(
        'Refresh consumer sentiment? This re-runs the analysis (billed) ' +
        'and replaces the current result.'
      );
      if (!confirmed) return;
    }
    setSentimentBusy(true);
    setSentimentMessage(null);
    try {
      const result = await api.analyzeBrandSentiment(id, force);
      setSentimentMessage({
        kind: 'ok',
        text: result.skipped
          ? (result.message || 'Already analyzed recently.')
          : 'Sentiment analysis finished.',
      });
      // Refresh the brand row so the card renders the new score.
      await loadBrandData();
    } catch (err) {
      setSentimentMessage({
        kind: 'err',
        text: err?.message || 'Sentiment analysis failed.',
      });
    } finally {
      setSentimentBusy(false);
    }
  };

  const handleRerunDeepResearch = async () => {
    const confirmed = window.confirm(
      "Refresh research fields? Changes to existing websites, social accounts " +
      "and brand names will go to Settings → Research changes for review. " +
      "Other research fields may be updated."
    );
    if (!confirmed) return;

    setDeepResearchPending(true);
    setDeepResearchMessage(null);
    try {
      const result = await api.rerunDeepResearch(id);
      setDeepResearchMessage({
        kind: 'ok',
        text: result.message || 'Deep research finished.',
      });
      // Refresh the brand row so newly-filled fields render.
      await loadBrandData();
    } catch (err) {
      setDeepResearchMessage({
        kind: 'err',
        text: err?.message || 'Deep research failed.',
      });
    } finally {
      setDeepResearchPending(false);
    }
  };

  const handleScoutContacts = async (override = {}) => {
    // Phase 1: pull the company roster + keyword-bucketed suggestions.
    // `override.domain` re-scopes the search when the brand's stored
    // website points at the wrong company. No enrichment spent here.
    setScoutPending(true);
    setContactMessage(null);
    try {
      const result = await api.scoutContacts(id, override);
      setCandidates(result);
      const n = (result.roster || []).length;
      setContactMessage({
        kind: n ? 'ok' : 'err',
        text: n
          ? `Found ${n} ${n === 1 ? 'person' : 'people'} at the company. Save who you need.`
          : `No people found${result.domain ? ` for ${result.domain}` : ''}. ${(result.notes || []).join(' ')}`,
      });
    } catch (err) {
      setContactMessage({
        kind: 'err',
        text: err?.message || 'Contact scout failed.',
      });
    } finally {
      setScoutPending(false);
    }
  };

  const handleSaveContact = async (role, candidate) => {
    // Save a person with a role tag. Inline email is reused for free.
    setContactBusyKey(`save:${candidate.linkedin_url || candidate.full_name || ''}`);
    setContactMessage(null);
    try {
      const result = await api.saveContact(id, {
        role,
        linkedin_url: candidate.linkedin_url,
        full_name: candidate.full_name,
        title: candidate.title,
        business_email: candidate.business_email,
      });
      setBrand((prev) => ({ ...prev, ...result.brand }));
      setContactMessage({ kind: 'ok', text: `Saved ${candidate.full_name || 'contact'}.` });
    } catch (err) {
      setContactMessage({ kind: 'err', text: err?.message || 'Could not save contact.' });
    } finally {
      setContactBusyKey(null);
    }
  };

  const handleFindEmail = async (candidate) => {
    // Look up a candidate's email (cascade lookup, billed only if a
    // verified email is found). A verified hit is auto-saved to the
    // brand's contacts by the backend — refresh so it shows in the
    // saved list. Returns { email, verified, guess } for the row.
    setContactBusyKey(`email:${candidate.linkedin_url || candidate.full_name || ''}`);
    setContactMessage(null);
    try {
      const result = await api.findContactEmail(id, candidate);
      if (result.brand) {
        setBrand((prev) => ({ ...prev, ...result.brand }));
      }
      if (!result.email && !result.guess) {
        setContactMessage({ kind: 'err', text: 'No email found for this person.' });
      } else if (result.verified) {
        setContactMessage({ kind: 'ok', text: 'Verified email found and saved to contacts.' });
      }
      return result;
    } catch (err) {
      setContactMessage({ kind: 'err', text: err?.message || 'Could not look up email.' });
      return null;
    } finally {
      setContactBusyKey(null);
    }
  };

  const handleGetPhone = async (contactId) => {
    // The pricey mobile lookup, on a saved contact, on demand.
    setContactBusyKey(`phone:${contactId}`);
    setContactMessage(null);
    try {
      const result = await api.fetchContactPhone(id, contactId);
      setBrand((prev) => ({ ...prev, ...result.brand }));
      setContactMessage({
        kind: result.phone ? 'ok' : 'err',
        text: result.phone ? 'Mobile number found.' : 'No mobile number found.',
      });
    } catch (err) {
      setContactMessage({ kind: 'err', text: err?.message || 'Could not fetch mobile.' });
    } finally {
      setContactBusyKey(null);
    }
  };

  const handleDeleteContact = async (contactId) => {
    setContactBusyKey(`delete:${contactId}`);
    setContactMessage(null);
    try {
      const result = await api.deleteContact(id, contactId);
      setBrand((prev) => ({ ...prev, ...result.brand }));
    } catch (err) {
      setContactMessage({ kind: 'err', text: err?.message || 'Could not remove contact.' });
    } finally {
      setContactBusyKey(null);
    }
  };

  useEffect(() => {
    loadBrandData();
  }, [id, platform, days]);

  // Movers' "Find contact" links to `/brands/:id#contacts`; the outreach
  // card is far enough down the page that landing at the top read as if
  // the link had done nothing. Scroll to it once the brand has rendered.
  const brandLoaded = !!brand;
  useEffect(() => {
    if (!brandLoaded || routeLocation.hash !== '#contacts') return;
    const el = document.getElementById('contacts');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [brandLoaded, routeLocation.hash]);

  const scrollToContacts = () => {
    const el = document.getElementById('contacts');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const loadBrandData = async () => {
    try {
      setLoading(true);

      const [brandData, growth, metricsData] = await Promise.all([
        api.getBrandFull(id),
        api.getBrandGrowth(id, platform, days),
        api.getBrandMetrics(id, { platform, days })
      ]);

      setBrand(brandData);
      setGrowthData(growth);
      setMetrics(metricsData.metrics);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async () => {
    if (!brand) return;
    const next = !brand.is_active;
    if (!next) {
      const confirmed = window.confirm(
        "Pause tracking for this brand? The weekly social sync will stop " +
        "pulling new metrics until you resume. Historical data is kept."
      );
      if (!confirmed) return;
    }
    setActivePending(true);
    setActionMessage(null);
    try {
      const updated = await api.setBrandActive(id, next);
      setBrand(prev => ({ ...prev, ...updated }));
      setActionMessage({
        kind: 'ok',
        text: next ? 'Tracking resumed.' : 'Tracking paused.',
      });
    } catch (err) {
      setActionMessage({
        kind: 'err',
        text: err?.message || 'Failed to update tracking.',
      });
    } finally {
      setActivePending(false);
    }
  };

  const handleDelete = async () => {
    if (!brand) return;
    const typed = window.prompt(
      `This permanently deletes "${brand.name}" and all its historical ` +
      `metrics. This cannot be undone.\n\n` +
      `Type the brand name to confirm:`
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== brand.name.trim().toLowerCase()) {
      setActionMessage({
        kind: 'err',
        text: 'Name did not match — brand was not deleted.',
      });
      return;
    }
    setDeleting(true);
    setActionMessage(null);
    try {
      await api.deleteBrand(id);
      navigate('/brands', {
        state: { flash: `Deleted ${brand.name}.` },
      });
    } catch (err) {
      setActionMessage({
        kind: 'err',
        text: err?.message || 'Delete failed.',
      });
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.exportMetrics(id, 'csv');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${brand?.name}_metrics.csv`;
      a.click();
    } catch (err) {
      alert('Export failed: ' + err.message);
    }
  };

  if (loading) {
    return (
      <OrganicShell active="brands" contentStyle={{ maxWidth: 1240 }}>
        <div className="card" style={{ padding: 'var(--space-4)', flexDirection: 'row', alignItems: 'center', gap: 8, fontSize: 14, opacity: 0.75 }}>
          <Loader2 size={16} strokeWidth={STROKE} className="spin" /> Loading brand details…
        </div>
      </OrganicShell>
    );
  }
  if (error) {
    return (
      <OrganicShell active="brands" contentStyle={{ maxWidth: 1240 }}>
        <div className="card" style={{ padding: 'var(--space-4)', color: 'var(--color-accent-700)', fontSize: 14 }}>
          Error: {error}
        </div>
      </OrganicShell>
    );
  }
  if (!brand) {
    return (
      <OrganicShell active="brands" contentStyle={{ maxWidth: 1240 }}>
        <div className="card" style={{ padding: 'var(--space-4)', color: 'var(--color-accent-700)', fontSize: 14 }}>
          Brand not found
        </div>
      </OrganicShell>
    );
  }

  // Chart data — Organic palette (canvas can't resolve CSS variables).
  const chartData = growthData?.data ? {
    labels: growthData.data.map(d => d.date),
    datasets: [{
      label: 'Followers',
      data: growthData.data.map(d => d.followers),
      borderColor: CHART_LINE,
      backgroundColor: CHART_FILL,
      fill: true,
      tension: 0.4,
      pointRadius: 0,
      pointHoverRadius: 4,
      borderWidth: 2.5,
    }]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `Followers: ${formatNumber(context.raw)}`
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: CHART_TICKS, maxTicksLimit: 8, font: { size: 11 } },
      },
      y: {
        beginAtZero: false,
        grid: { color: CHART_GRID },
        ticks: {
          color: CHART_TICKS,
          font: { size: 11 },
          callback: (value) => formatNumber(value)
        }
      }
    }
  };

  const latestMetric = metrics[0];
  const previousMetric = metrics[metrics.length - 1];
  const sentiment = brand?.sentiment;
  const followerGrowth = latestMetric?.followers && previousMetric?.followers
    ? ((latestMetric.followers - previousMetric.followers) / previousMetric.followers * 100).toFixed(2)
    : 0;
  const followerDelta = latestMetric?.followers && previousMetric?.followers
    ? latestMetric.followers - previousMetric.followers
    : null;

  const platformLabel = platform === 'tiktok' ? 'TikTok' : 'Instagram';
  const formerly = formerNamesLabel(brand);
  const location = [brand.hq_city, brand.hq_state, brand.country].filter(Boolean).join(', ');
  const metaParts = [];
  if (brand.category) metaParts.push(brand.category);
  if (location) metaParts.push(location);
  if (brand.founded_year) metaParts.push(`founded ${brand.founded_year}`);
  if (formerly) metaParts.push(`formerly ${formerly}`);

  const hasSentimentScore = sentiment && sentiment.label !== 'Insufficient' && sentiment.overall_score != null;
  // An unscored run still returns themes when it found *some* review text.
  // The scored card always shows both theme columns (with "—" when empty);
  // the unscored one shows them only when there is something in them, so a
  // genuinely empty result doesn't render two blank headings.
  const hasSentimentThemes = !!(
    (sentiment?.positive_themes || []).length || (sentiment?.negative_themes || []).length
  );
  const founderList = brand.founders
    ? (Array.isArray(brand.founder_linkedin_urls) && brand.founder_linkedin_urls.length > 0
      ? brand.founder_linkedin_urls
      : brand.founders.split(',').map(n => ({ name: n.trim(), linkedin_url: null })))
    : [];

  // The six header actions don't fit beside the title on a phone: Share
  // and Find contact become the two buttons under the title, and the
  // rest move into the top bar's overflow menu.
  const overflowActions = [
    isSuperAdmin() && {
      key: 'research',
      label: deepResearchPending ? 'Researching…' : 'Re-research',
      icon: deepResearchPending
        ? <Loader2 size={14} strokeWidth={STROKE} className="spin" />
        : <RefreshCw size={14} strokeWidth={STROKE} />,
      disabled: deepResearchPending,
      onClick: handleRerunDeepResearch,
    },
    {
      key: 'edit',
      label: 'Edit',
      icon: <Pencil size={14} strokeWidth={STROKE} />,
      disabled: deleting,
      onClick: () => setEditing(true),
    },
    {
      key: 'active',
      label: brand.is_active === false ? 'Resume tracking' : 'Pause tracking',
      icon: activePending
        ? <Loader2 size={14} strokeWidth={STROKE} className="spin" />
        : (brand.is_active === false
          ? <Play size={14} strokeWidth={STROKE} />
          : <Pause size={14} strokeWidth={STROKE} />),
      disabled: activePending || deleting,
      onClick: handleToggleActive,
    },
    {
      key: 'export',
      label: 'Export metrics',
      icon: <Download size={14} strokeWidth={STROKE} />,
      onClick: handleExport,
    },
    {
      key: 'delete',
      label: 'Delete brand',
      icon: deleting
        ? <Loader2 size={14} strokeWidth={STROKE} className="spin" />
        : <Trash2 size={14} strokeWidth={STROKE} />,
      disabled: deleting,
      danger: true,
      onClick: handleDelete,
    },
  ].filter(Boolean);

  const mobileTopBar = (
    <div className="o-topbar">
      <Link
        to="/brands"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, height: 44,
          fontSize: 14, textDecoration: 'none',
        }}
      >
        <ArrowLeft size={16} strokeWidth={STROKE} /> Brands
      </Link>
      <div style={{ marginLeft: 'auto', position: 'relative', flex: 'none' }}>
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          style={{ width: 44, height: 44 }}
          aria-label="More actions"
          aria-expanded={actionsOpen}
          onClick={() => setActionsOpen((v) => !v)}
        >
          <MoreHorizontal size={18} strokeWidth={STROKE} />
        </button>
        {actionsOpen && (
          <div
            className="card elev-lg"
            style={{
              position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 50,
              padding: 'var(--space-2)', minWidth: 210, gap: 2,
              background: 'var(--color-neutral-100)', border: '1px solid var(--color-divider)',
            }}
          >
            {overflowActions.map((action) => (
              <button
                key={action.key}
                type="button"
                className="o-account-menu-item"
                disabled={action.disabled}
                style={action.danger ? { color: 'var(--color-accent-700)' } : undefined}
                onClick={() => { setActionsOpen(false); action.onClick(); }}
              >
                {action.icon} {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <OrganicShell active="brands" contentStyle={{ maxWidth: 1240 }} mobileTopBar={mobileTopBar}>
      {!isMobile && (
        <Link to="/brands" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, alignSelf: 'flex-start' }}>
          <ArrowLeft size={15} strokeWidth={STROKE} /> Back to Brands
        </Link>
      )}

      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4) var(--space-6)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 340px', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: isMobile ? 32 : 44 }}>{brand.name}</h1>
            {brand.is_active === false ? (
              <span
                className="tag tag-neutral"
                style={{ gap: 5 }}
                title="Weekly social sync is paused for this brand. Historical metrics remain visible."
              >
                <Pause size={11} strokeWidth={STROKE} /> Tracking paused
              </span>
            ) : (
              <span className="tag tag-accent-2">Active</span>
            )}
          </div>
          {metaParts.length > 0 && (
            <p
              style={{ margin: 0, fontSize: 14, opacity: 0.75 }}
              title={formerly
                ? 'Previous brand name(s) found by deep research — retailer listings may still use them.'
                : undefined}
            >
              {metaParts.join(' · ')}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', fontSize: 13 }}>
            {brand.website && (
              <a href={brand.website} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Globe size={14} strokeWidth={STROKE} /> {brand.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </a>
            )}
            {brand.instagram_handle && (
              <a href={`https://instagram.com/${brand.instagram_handle}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Instagram size={14} strokeWidth={STROKE} /> @{brand.instagram_handle}
              </a>
            )}
            {brand.tiktok_handle && (
              <a href={`https://tiktok.com/@${brand.tiktok_handle}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Video size={14} strokeWidth={STROKE} /> TikTok
              </a>
            )}
            {brand.linkedin_url && (
              <a href={brand.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Linkedin size={14} strokeWidth={STROKE} /> LinkedIn
              </a>
            )}
            {brand.youtube_url && (
              <a href={brand.youtube_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Youtube size={14} strokeWidth={STROKE} /> YouTube
              </a>
            )}
          </div>
          <div style={{ marginTop: 'var(--space-1)' }}>
            <BrandTagEditor
              brand={brand}
              small={false}
              onBrandUpdated={(updated) => setBrand((prev) => ({ ...prev, ...updated }))}
            />
          </div>
        </div>

        {/* Find contacts lives on the outreach card itself, next to the
            contacts it produces — this row is brand-level admin only.
            On a phone it lives in the top bar's overflow menu instead. */}
        <div style={{ display: isMobile ? 'none' : 'flex', flexDirection: 'column', gap: 'var(--space-2)', flex: 'none', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 13 }}
              onClick={() => setShowShare(true)}
              title="Email this brand's intelligence report — profile, links, metrics and discovered contacts — to a commercial colleague."
            >
              <Share2 size={15} strokeWidth={STROKE} /> Share
            </button>
            {isSuperAdmin() && (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: 13 }}
                onClick={handleRerunDeepResearch}
                disabled={deepResearchPending}
                title="Refresh research. Existing website, social account and name changes require super-admin approval in Settings → Research changes."
              >
                {deepResearchPending
                  ? <><Loader2 size={15} strokeWidth={STROKE} className="spin" /> Researching…</>
                  : <><RefreshCw size={15} strokeWidth={STROKE} /> <span style={{ whiteSpace: 'nowrap' }}>Re-research</span></>}
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 13 }}
              onClick={() => setEditing(true)}
              disabled={deleting}
            >
              <Pencil size={15} strokeWidth={STROKE} /> Edit
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 13 }}
              onClick={handleToggleActive}
              disabled={activePending || deleting}
              title={
                brand.is_active === false
                  ? 'Resume the weekly social sync for this brand.'
                  : 'Stop pulling new social metrics for this brand. Historical data is kept.'
              }
            >
              {activePending
                ? <Loader2 size={15} strokeWidth={STROKE} className="spin" />
                : (brand.is_active === false
                  ? <Play size={15} strokeWidth={STROKE} />
                  : <Pause size={15} strokeWidth={STROKE} />)}
              {brand.is_active === false ? 'Resume tracking' : 'Pause tracking'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 13 }}
              onClick={handleExport}
              title="Export this brand's metrics history as CSV."
            >
              <Download size={15} strokeWidth={STROKE} /> Export
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 13, color: 'var(--color-accent-700)' }}
              onClick={handleDelete}
              disabled={deleting}
              title="Permanently delete this brand and all its historical metrics."
            >
              {deleting
                ? <Loader2 size={15} strokeWidth={STROKE} className="spin" />
                : <Trash2 size={15} strokeWidth={STROKE} />}
              Delete
            </button>
          </div>
        </div>
      </header>

      {isMobile && (
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            type="button"
            className="btn btn-primary"
            style={{ flex: 1, height: 48, minWidth: 0 }}
            onClick={scrollToContacts}
          >
            <UserSearch size={16} strokeWidth={STROKE} /> Find contact
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ flex: 1, height: 48, minWidth: 0 }}
            onClick={() => setShowShare(true)}
          >
            <Share2 size={16} strokeWidth={STROKE} /> Share
          </button>
        </div>
      )}

      {actionMessage && <Notice kind={actionMessage.kind}>{actionMessage.text}</Notice>}
      {deepResearchMessage && <Notice kind={deepResearchMessage.kind}>{deepResearchMessage.text}</Notice>}

      {/* Metric cards — a 2-up grid on a phone (see .o-metric-grid). */}
      <div className="o-metric-grid">
        <div className="card" style={{ gap: 2, padding: 'var(--space-4)' }}>
          <span className="card-kicker">Followers</span>
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: isMobile ? 26 : 30 }}>
            {latestMetric ? formatNumber(latestMetric.followers) : '—'}
          </span>
          <span style={{ fontSize: 12, opacity: 0.65 }}>{platformLabel}</span>
        </div>
        <div className="card" style={{ gap: 2, padding: 'var(--space-4)' }}>
          <span className="card-kicker">Growth, {days} days</span>
          <span style={{
            fontFamily: 'var(--font-heading)', fontSize: isMobile ? 26 : 30,
            color: followerGrowth >= 0 ? 'var(--color-accent-2-700)' : 'var(--color-accent-700)',
          }}>
            {latestMetric ? `${followerGrowth >= 0 ? '+' : ''}${followerGrowth}%` : '—'}
          </span>
          <span style={{ fontSize: 12, opacity: 0.65 }}>
            {followerDelta != null
              ? `${followerDelta >= 0 ? '+' : ''}${followerDelta.toLocaleString()} followers`
              : platformLabel}
          </span>
        </div>
        <div className="card" style={{ gap: 2, padding: 'var(--space-4)' }}>
          <span className="card-kicker">Vs baseline</span>
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: isMobile ? 26 : 30 }}>—</span>
          <span style={{ fontSize: 12, opacity: 0.65 }}>4-week average</span>
        </div>
        <div className="card" style={{ gap: 2, padding: 'var(--space-4)' }}>
          <span className="card-kicker">TikTok</span>
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: isMobile ? 26 : 30 }}>
            {platform === 'tiktok' && latestMetric ? formatNumber(latestMetric.followers) : '—'}
          </span>
          <span style={{ fontSize: 12, opacity: 0.65 }}>
            {platform === 'tiktok' && latestMetric
              ? 'followers'
              : (brand.tiktok_handle
                ? `@${brand.tiktok_handle} — switch the chart to TikTok`
                : 'No TikTok handle')}
          </span>
        </div>
        <div className="card" style={{ gap: 2, padding: 'var(--space-4)' }}>
          <span className="card-kicker">Sentiment</span>
          <span style={{
            fontFamily: 'var(--font-heading)', fontSize: isMobile ? 26 : 30,
            color: hasSentimentScore ? sentimentScoreColor(sentiment.overall_score) : undefined,
          }}>
            {hasSentimentScore ? Math.round(sentiment.overall_score) : '—'}
          </span>
          <span style={{ fontSize: 12, opacity: 0.65 }}>
            {/* review_volume counts independent sources only, so a
                first-party-scored brand reports its own-site count here
                rather than the "0 reviews" that would sit beside a score. */}
            {sentiment
              ? `${sentiment.label}${sentimentVolumeLabel(sentiment)}`
              : 'No analysis yet'}
          </span>
        </div>
      </div>

      {/* Follower growth chart */}
      <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-4)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <div>
            <h4 style={{ margin: 0 }}>Follower growth</h4>
            <p style={{ margin: '2px 0 0', fontSize: 13, opacity: 0.65 }}>
              Daily follower count, last {days} days on {platformLabel}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="seg">
              <label className="seg-opt">
                <input
                  type="radio"
                  name="bdPlatform"
                  checked={platform === 'instagram'}
                  onChange={() => setPlatform('instagram')}
                />
                Instagram
              </label>
              <label className="seg-opt">
                <input
                  type="radio"
                  name="bdPlatform"
                  checked={platform === 'tiktok'}
                  onChange={() => setPlatform('tiktok')}
                />
                TikTok
              </label>
            </div>
            <div className="seg" style={{ whiteSpace: 'nowrap' }}>
              <label className="seg-opt" style={{ whiteSpace: 'nowrap' }}>
                <input
                  type="radio"
                  name="bdRange"
                  checked={days === 30}
                  onChange={() => setDays(30)}
                />
                30 days
              </label>
              <label className="seg-opt" style={{ whiteSpace: 'nowrap' }}>
                <input
                  type="radio"
                  name="bdRange"
                  checked={days === 90}
                  onChange={() => setDays(90)}
                />
                90 days
              </label>
            </div>
          </div>
        </div>
        {chartData ? (
          <div style={{ height: 180 }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 13, opacity: 0.65 }}>
            No metrics available for this platform
          </p>
        )}
      </div>

      {/* Outreach contacts + Products */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))', gap: 'var(--space-4)' }}>
        <OutreachContacts
          brand={brand}
          candidates={candidates}
          busyKey={contactBusyKey}
          scoutPending={scoutPending}
          message={contactMessage}
          onScout={() => handleScoutContacts()}
          onRescout={(domain) => handleScoutContacts({ domain })}
          onSave={handleSaveContact}
          onFindEmail={handleFindEmail}
          onGetPhone={handleGetPhone}
          onDelete={handleDeleteContact}
        />
        <ProductsCard brand={brand} onBrandUpdate={setBrand} />
      </div>

      {/* Comments + Following */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))', gap: 'var(--space-4)' }}>
        <BrandComments brand={brand} onBrandUpdate={setBrand} />
        <TeamFollowers brand={brand} onBrandUpdate={setBrand} onShare={() => setShowShare(true)} />
      </div>

      {/* Brand information + Consumer sentiment */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))', gap: 'var(--space-4)' }}>
        <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
          <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Globe size={18} strokeWidth={STROKE} /> Brand information
          </h4>

          {founderList.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', fontSize: 14 }}>
              <Users size={15} strokeWidth={STROKE} style={{ opacity: 0.55, flex: 'none', marginTop: 3 }} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
                <span>Founded by</span>
                {founderList.map((f, idx, arr) => (
                  <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <span>{f.name}</span>
                    {f.linkedin_url && (
                      <a
                        href={f.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${f.name} on LinkedIn`}
                        style={{ display: 'inline-flex' }}
                      >
                        <Linkedin size={13} strokeWidth={STROKE} />
                      </a>
                    )}
                    {idx < arr.length - 1 && <span style={{ opacity: 0.55 }}>,</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {brand.parent_company && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 14 }}>
              <Globe size={15} strokeWidth={STROKE} style={{ opacity: 0.55, flex: 'none' }} />
              <span>Parent: {brand.parent_company}</span>
            </div>
          )}

          {brand.revenue && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 14 }}>
              <TrendingUp size={15} strokeWidth={STROKE} style={{ opacity: 0.55, flex: 'none' }} />
              <span>Revenue: {brand.revenue}</span>
            </div>
          )}

          {Array.isArray(brand.marketplaces) && brand.marketplaces.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', fontSize: 14 }}>
              <ShoppingBag size={15} strokeWidth={STROKE} style={{ opacity: 0.55, flex: 'none', marginTop: 3 }} />
              <div className="o-chip-row" style={{ alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>Available on:</span>
                {brand.marketplaces.map(name => {
                  const url = (brand.marketplace_urls || {})[name];
                  return url ? (
                    <a
                      key={name}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tag tag-accent"
                      style={{ gap: 4, textDecoration: 'none' }}
                      title={`View ${name} product page`}
                    >
                      {name} <ExternalLink size={11} strokeWidth={STROKE} />
                    </a>
                  ) : (
                    <span key={name} className="tag tag-accent">{name}</span>
                  );
                })}
              </div>
            </div>
          )}

          {brand.notes && (
            <div style={{ padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', background: 'var(--color-neutral-200)', fontSize: 14 }}>
              {brand.notes}
            </div>
          )}

          <span
            style={{ fontSize: 11, opacity: 0.55, display: 'inline-flex', alignItems: 'center', gap: 5 }}
            title={
              brand.last_deep_research_at
                ? new Date(brand.last_deep_research_at).toLocaleString()
                : undefined
            }
          >
            <Clock size={12} strokeWidth={STROKE} />
            Last deep research:{' '}
            {brand.last_deep_research_at
              ? formatRelativeTime(brand.last_deep_research_at)
              : 'never'}
          </span>
        </div>

        <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <MessageSquare size={18} strokeWidth={STROKE} /> Consumer product sentiment
            </h4>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 13 }}
              onClick={handleAnalyzeSentiment}
              disabled={sentimentBusy}
            >
              {sentimentBusy ? (
                <><Loader2 size={14} strokeWidth={STROKE} className="spin" /> Analyzing…</>
              ) : (
                <><RefreshCw size={14} strokeWidth={STROKE} /> {sentiment ? 'Refresh' : 'Analyze'}</>
              )}
            </button>
          </div>

          {sentimentMessage && <Notice kind={sentimentMessage.kind}>{sentimentMessage.text}</Notice>}

          {sentiment ? (
            <>
              {/* Thin evidence withholds the numbers, not the research: the
                  run still cost a credit and still came back with themes, a
                  summary and any brand-site rating, so those stay on the card
                  under a plain statement of why there is no score. */}
              {sentiment.label === 'Insufficient' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span className="card-kicker">Overall</span>
                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: 24, opacity: 0.5 }}>
                    —<span style={{ fontSize: 13 }}> not scored</span>
                  </span>
                  <p style={{ margin: 0, fontSize: 13, opacity: 0.75, textWrap: 'pretty' }}>
                    {insufficientReason(sentiment)}
                  </p>
                </div>
              ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 'var(--space-3)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span className="card-kicker">Overall</span>
                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: 24, color: sentimentScoreColor(sentiment.overall_score) }}>
                    {Math.round(sentiment.overall_score)}<span style={{ fontSize: 13, opacity: 0.6 }}>/100</span>
                  </span>
                  <span style={{ fontSize: 12, color: sentimentLabelColor(sentiment.label), opacity: sentimentLabelColor(sentiment.label) ? 1 : 0.65 }}>
                    {sentiment.label}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span className="card-kicker">Product quality</span>
                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: 24 }}>
                    {sentiment.product_score != null ? Math.round(sentiment.product_score) : '—'}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span className="card-kicker">Avg stars</span>
                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: 24 }}>
                    {sentiment.review_star_avg != null ? `${sentiment.review_star_avg.toFixed(1)}★` : '—'}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span className="card-kicker">Reviews seen</span>
                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: 24 }}>
                    {sentiment.review_volume != null ? formatNumber(sentiment.review_volume) : '—'}
                  </span>
                </div>
              </div>
              )}

              {(sentiment.first_party_star_avg != null || sentiment.first_party_review_volume != null) && (
                <span style={{ fontSize: 12, opacity: 0.7 }}>
                  {"Brand's own site: "}
                  {sentiment.first_party_star_avg != null ? `${sentiment.first_party_star_avg.toFixed(1)}★` : '—'}
                  {sentiment.first_party_review_volume != null && ` · ${formatNumber(sentiment.first_party_review_volume)} reviews`}
                  {' '}
                  <span style={{ opacity: 0.75 }}>
                    {sentiment.label === 'Insufficient'
                      ? '— all the review evidence found, and too little of it to score. The brand moderates these, so treat them as directional only.'
                      : sentiment.score_basis === 'first_party'
                        ? '— no independent reviews found, so the score above rests on these. The brand moderates them: treat it as a weak signal.'
                        : '— first-party and moderated by the brand, so it is shown for context only and does not affect the score above.'}
                  </span>
                </span>
              )}

              {sentiment.top_reason && (
                <p style={{ margin: 0, fontSize: 13, opacity: 0.75, textWrap: 'pretty' }}>
                  {sentiment.top_reason}
                </p>
              )}

              {(sentiment.label !== 'Insufficient' || hasSentimentThemes) && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-3)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  <span className="card-kicker" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <ThumbsUp size={12} strokeWidth={STROKE} /> What consumers like
                  </span>
                  <div className="o-chip-row">
                    {(sentiment.positive_themes || []).length ? (
                      sentiment.positive_themes.map((t, i) => (
                        <SentimentThemeChip key={i} theme={t} kind="positive" />
                      ))
                    ) : (
                      <span style={{ fontSize: 13, opacity: 0.65 }}>—</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  <span className="card-kicker" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <ThumbsDown size={12} strokeWidth={STROKE} /> Common complaints
                  </span>
                  <div className="o-chip-row">
                    {(sentiment.negative_themes || []).length ? (
                      sentiment.negative_themes.map((t, i) => (
                        <SentimentThemeChip key={i} theme={t} kind="negative" />
                      ))
                    ) : (
                      <span style={{ fontSize: 13, opacity: 0.65 }}>—</span>
                    )}
                  </div>
                </div>
              </div>
              )}

              <span style={{ fontSize: 11, opacity: 0.55 }}>
                Analyzed {formatRelativeTime(sentiment.computed_at) || '—'}
                {sentiment.label !== 'Insufficient' && sentiment.confidence != null
                  && ` · confidence ${Math.round(sentiment.confidence * 100)}%`}
                {sentiment.label === 'Insufficient'
                  ? ' · searched retailer reviews, Reddit & press'
                  : sentiment.score_basis === 'first_party'
                    ? " · scored from the brand's own site (no independent reviews found)"
                    : ' · scored from retailer reviews, Reddit & press'}
              </span>
            </>
          ) : (
            <p style={{ margin: 0, fontSize: 13, opacity: 0.65, textWrap: 'pretty' }}>
              No sentiment analysis yet. Click Analyze to research consumer reviews for this brand.
            </p>
          )}
        </div>
      </div>

      {/* Metrics history */}
      <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
        <h4 style={{ margin: 0 }}>Metrics history</h4>
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Platform</th>
                <th>Followers</th>
                <th>Following</th>
                <th>Posts</th>
                <th>Engagement Rate</th>
              </tr>
            </thead>
            <tbody>
              {metrics.slice(0, 20).map(metric => (
                <tr key={metric.id}>
                  <td>{new Date(metric.recorded_at).toLocaleDateString()}</td>
                  <td>{metric.platform}</td>
                  <td>{formatNumber(metric.followers)}</td>
                  <td>{formatNumber(metric.following)}</td>
                  <td>{formatNumber(metric.posts)}</td>
                  <td>{metric.engagement_rate != null ? `${metric.engagement_rate.toFixed(2)}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditBrandModal
          brand={brand}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            setBrand(prev => ({ ...prev, ...updated }));
            setEditing(false);
            setActionMessage({ kind: 'ok', text: `Updated ${updated.name}.` });
          }}
        />
      )}

      {showShare && (
        <ShareBrandModal
          brand={brand}
          onClose={() => setShowShare(false)}
          onShared={(updated) => setBrand((prev) => ({ ...prev, ...updated }))}
        />
      )}
    </OrganicShell>
  );
}

function joinFounderLinkedinUrls(brand) {
  if (!Array.isArray(brand.founder_linkedin_urls) || brand.founder_linkedin_urls.length === 0) {
    return '';
  }
  return brand.founder_linkedin_urls.map(f => f.linkedin_url || '').join(', ');
}

function EditBrandModal({ brand, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: brand.name || '',
    category: brand.category || '',
    notes: brand.notes || '',
    website: brand.website || '',
    instagram_handle: brand.instagram_handle || '',
    tiktok_handle: brand.tiktok_handle || '',
    linkedin_url: brand.linkedin_url || '',
    youtube_url: brand.youtube_url || '',
    country: brand.country || '',
    hq_city: brand.hq_city || '',
    hq_state: brand.hq_state || '',
    founders: brand.founders || '',
    founder_linkedin_urls: joinFounderLinkedinUrls(brand),
    founded_year: brand.founded_year ? String(brand.founded_year) : '',
    parent_company: brand.parent_company || '',
    revenue: brand.revenue || '',
    funding: brand.funding || '',
    former_names: brand.former_names || '',
    marketplaces: (Array.isArray(brand.marketplaces) ? brand.marketplaces : []).join(', '),
    // Retailer → URL map edited as "Retailer: https://…" lines.
    marketplace_urls_text: Object.entries(brand.marketplace_urls || {})
      .map(([k, v]) => `${k}: ${v}`)
      .join('\n'),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const setField = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!form.category.trim()) {
      setError('Category is required.');
      return;
    }
    setSaving(true);
    setError(null);
    const payload = { ...form };
    // "Retailer: URL" lines → dict. Lines without a URL are dropped;
    // an empty textarea clears the stored map.
    delete payload.marketplace_urls_text;
    const urlMap = {};
    form.marketplace_urls_text.split('\n').forEach((line) => {
      const idx = line.indexOf(':');
      if (idx > 0) {
        const key = line.slice(0, idx).trim();
        const url = line.slice(idx + 1).trim();
        if (key && url) urlMap[key] = url;
      }
    });
    payload.marketplace_urls = urlMap;
    if (form.founded_year === '') {
      payload.founded_year = null;
    } else {
      const yr = Number(form.founded_year);
      if (Number.isFinite(yr)) {
        payload.founded_year = yr;
      } else {
        setError('Founded year must be a number.');
        setSaving(false);
        return;
      }
    }
    try {
      const updated = await api.updateBrand(brand.id, payload);
      onSaved(updated);
    } catch (err) {
      setError(err?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div
        className="dialog"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(760px, 100%)', maxHeight: '85vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="dialog-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pencil size={18} strokeWidth={STROKE} /> Edit brand
          </div>
          <button type="button" className="btn btn-icon btn-secondary" aria-label="Close" onClick={onClose}>
            <X size={16} strokeWidth={STROKE} />
          </button>
        </div>

        {error && <Notice kind="err">{error}</Notice>}

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-3)' }}>
          <Field label="Name">
            <input className="input" value={form.name} onChange={setField('name')} />
          </Field>
          <Field label="Category">
            <input className="input" value={form.category} onChange={setField('category')} />
          </Field>
        </div>

        <Field label="Description">
          <textarea className="input" rows={3} value={form.notes} onChange={setField('notes')} />
        </Field>

        <Field label="Website">
          <input className="input" value={form.website} onChange={setField('website')} placeholder="https://" />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <Field label="Instagram handle" hint="No leading @">
            <input className="input" value={form.instagram_handle} onChange={setField('instagram_handle')} />
          </Field>
          <Field label="TikTok handle" hint="No leading @">
            <input className="input" value={form.tiktok_handle} onChange={setField('tiktok_handle')} />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <Field label="LinkedIn URL">
            <input className="input" value={form.linkedin_url} onChange={setField('linkedin_url')} placeholder="https://linkedin.com/company/…" />
          </Field>
          <Field label="YouTube URL">
            <input className="input" value={form.youtube_url} onChange={setField('youtube_url')} placeholder="https://youtube.com/@…" />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-3)' }}>
          <Field label="Country">
            <input className="input" value={form.country} onChange={setField('country')} />
          </Field>
          <Field label="HQ city">
            <input className="input" value={form.hq_city} onChange={setField('hq_city')} />
          </Field>
          <Field label="HQ state">
            <input className="input" value={form.hq_state} onChange={setField('hq_state')} />
          </Field>
        </div>

        <Field label="Founders (comma-separated)">
          <input className="input" value={form.founders} onChange={setField('founders')} />
        </Field>
        <Field
          label="Founder LinkedIn URLs (comma-separated, aligned with founders)"
          hint="Use empty slots for founders without a profile, e.g. https://..., , https://..."
        >
          <input className="input" value={form.founder_linkedin_urls} onChange={setField('founder_linkedin_urls')} />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--space-3)' }}>
          <Field label="Founded year">
            <input className="input" type="number" value={form.founded_year} onChange={setField('founded_year')} />
          </Field>
          <Field label="Parent company">
            <input className="input" value={form.parent_company} onChange={setField('parent_company')} />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <Field label="Revenue">
            <input className="input" value={form.revenue} onChange={setField('revenue')} />
          </Field>
          <Field label="Funding">
            <input className="input" value={form.funding} onChange={setField('funding')} />
          </Field>
        </div>

        <Field label="Former names (comma-separated)" hint="Previous names when the brand has rebranded — shown as “formerly X”.">
          <input className="input" value={form.former_names} onChange={setField('former_names')} />
        </Field>

        <Field label="Marketplaces (comma-separated)" hint="Retailers the brand is available on, e.g. Walmart, Target. Clear to remove a wrongly-detected retailer.">
          <input className="input" value={form.marketplaces} onChange={setField('marketplaces')} />
        </Field>

        <Field
          label="Marketplace product-page URLs"
          hint={'One per line as "Retailer: URL", e.g. Walmart: https://walmart.com/ip/…'}
        >
          <textarea
            className="input"
            rows={3}
            value={form.marketplace_urls_text}
            onChange={setField('marketplace_urls_text')}
          />
        </Field>

        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving
              ? <Loader2 size={14} strokeWidth={STROKE} className="spin" />
              : <CheckCircle2 size={14} strokeWidth={STROKE} />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="field" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, color: 'color-mix(in srgb, var(--color-text) 70%, transparent)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 11, opacity: 0.6 }}>{hint}</span>}
    </label>
  );
}

export default BrandDetail;

